import { z } from 'zod';

/**
 * A B2B sales-pipeline lead — deliberately a separate concept from
 * `Lead` (`lead.schema.ts`), which is the public contact-form submission.
 * Merging them would conflate two different lifecycles (anonymous public
 * inquiry vs. an owned, staged sales record); see the CRM plan for the
 * reasoning. Ported from Office_project's `Lead` model, renamed to avoid
 * the collision.
 */
export const SALES_LEAD_SOURCES = [
  'website',
  'referral',
  'cold_call',
  'email_campaign',
  'social_media',
  'trade_show',
  'advertisement',
  'partner',
  'other',
] as const;
export const salesLeadSourceEnum = z.enum(SALES_LEAD_SOURCES);
export type SalesLeadSource = z.infer<typeof salesLeadSourceEnum>;

export const SALES_LEAD_STATUSES = [
  'new',
  'contacted',
  'qualified',
  'proposal_sent',
  'negotiation',
  'converted',
  'lost',
  'unqualified',
] as const;
export const salesLeadStatusEnum = z.enum(SALES_LEAD_STATUSES);
export type SalesLeadStatus = z.infer<typeof salesLeadStatusEnum>;

export const SALES_LEAD_PRIORITIES = ['low', 'medium', 'high'] as const;
export const salesLeadPriorityEnum = z.enum(SALES_LEAD_PRIORITIES);
export type SalesLeadPriority = z.infer<typeof salesLeadPriorityEnum>;

export const createSalesLeadSchema = z.object({
  name: z.string().trim().min(2, 'Name must be at least 2 characters').max(120),
  email: z
    .string()
    .trim()
    .toLowerCase()
    .email('Enter a valid email address')
    .optional()
    .or(z.literal('')),
  phone: z.string().trim().max(20).optional().or(z.literal('')),
  company: z.string().trim().max(160).optional().or(z.literal('')),
  position: z.string().trim().max(120).optional().or(z.literal('')),

  source: salesLeadSourceEnum.default('other'),
  status: salesLeadStatusEnum.default('new'),
  priority: salesLeadPriorityEnum.default('medium'),
  estimatedValue: z.coerce.number().min(0).default(0),

  assignedTo: z.string().optional(),

  nextFollowUpDate: z.coerce.date().optional(),

  lostReason: z.string().trim().max(500).optional().or(z.literal('')),
  notes: z.string().trim().max(2000).optional().or(z.literal('')),
  tags: z.array(z.string().trim().max(40)).default([]),

  city: z.string().trim().max(100).optional().or(z.literal('')),
  state: z.string().trim().max(100).optional().or(z.literal('')),
  country: z.string().trim().max(100).default('India'),
});

export type CreateSalesLeadInput = z.infer<typeof createSalesLeadSchema>;

export const updateSalesLeadSchema = createSalesLeadSchema.partial();
export type UpdateSalesLeadInput = z.infer<typeof updateSalesLeadSchema>;

export const salesLeadListQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(20),
  status: salesLeadStatusEnum.optional(),
  source: salesLeadSourceEnum.optional(),
  priority: salesLeadPriorityEnum.optional(),
  assignedTo: z.string().optional(),
  search: z.string().trim().max(200).optional(),
});

export type SalesLeadListQuery = z.infer<typeof salesLeadListQuerySchema>;

export const assignSalesLeadSchema = z.object({
  userId: z.string().min(1, 'userId is required'),
});

export type AssignSalesLeadInput = z.infer<typeof assignSalesLeadSchema>;
